import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Home, 
  Search, 
  ArrowRight,
  Compass,
  AlertTriangle,
  Zap,
  Sparkles
} from 'lucide-react';

// Constants outside component to prevent recreation
const QUICK_LINKS = [
  { path: '/tools', label: 'AI Tools Directory', icon: <Search className="w-4 h-4" /> },
  { path: '/categories', label: 'Categories', icon: <Compass className="w-4 h-4" /> },
  { path: '/blog', label: 'AI Blog', icon: <Zap className="w-4 h-4" /> },
] as const;

const STATS = [
  { value: '25,000+', label: 'AI Tools', color: 'purple' },
  { value: '150+', label: 'Categories', color: 'blue' }
] as const;

const NotFound: React.FC = () => {
  // Memoized SEO data
  const seoData = useMemo(() => ({
    title: '404 Page Not Found | TooliQ.ai AI Tools Directory',
    description: 'The page you\'re looking for doesn\'t exist. Explore our AI tools directory with 25,000+ tools or return to the homepage.',
    canonical: 'https://tooliq.ai/404',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "404 Page Not Found - TooliQ.ai",
      "description": "Custom 404 error page for TooliQ.ai AI tools directory",
      "url": "https://tooliq.ai/404",
      "mainEntity": {
        "@type": "WebSite",
        "name": "TooliQ.ai AI Tools Directory",
        "url": "https://tooliq.ai",
        "description": "Discover 25,000+ AI tools across 150+ categories"
      }
    }
  }), []);

  // Event handler for back button
  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-indigo-900 flex items-center justify-center px-4 py-8">
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonical} />
        
        {/* CRITICAL: Add noindex to prevent indexing of 404 pages */}
        <meta name="robots" content="noindex, follow" />

        {/* Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(seoData.structuredData)
          }}
        />
      </Helmet>

      <div className="max-w-2xl mx-auto text-center relative z-10">
        {/* Animated 404 Graphic */}
        <ErrorGraphic />

        {/* Main Content with SEO improvements */}
        <div className="space-y-6 mb-8">
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent">
            Page Not Found
          </h1>
          
          <SEOContent />
        </div>

        {/* Quick Links - Improved for SEO */}
        <QuickLinksSection />

        {/* Primary Action Buttons */}
        <PrimaryActions onGoBack={handleGoBack} />

        {/* AI Tools Search CTA */}
        <AIToolsCTA />

        {/* Help Section */}
        <HelpSection />

        {/* Stats Section - Good for keeping users engaged */}
        <StatsSection />

        {/* Decorative Elements */}
        <DecorativeSection />
      </div>

      {/* Background Animation */}
      <BackgroundAnimation />
    </div>
  );
};

// Extracted ErrorGraphic component
const ErrorGraphic: React.FC = () => (
  <div className="relative mb-8">
    <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-500 rounded-full blur-3xl opacity-20 animate-pulse"></div>
    <div className="relative">
      <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/20 dark:to-orange-900/20 rounded-3xl border-8 border-white dark:border-gray-800 shadow-2xl mb-6">
        <AlertTriangle className="w-16 h-16 text-red-500 dark:text-red-400" />
      </div>
      <div className="absolute -top-4 -right-4">
        <div className="text-8xl font-black bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
          404
        </div>
      </div>
    </div>
  </div>
);

// Extracted SEOContent component
const SEOContent: React.FC = () => (
  <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/10 dark:to-purple-900/10 rounded-2xl p-6 border border-gray-200/50 dark:border-gray-700/50">
    <p className="text-lg text-slate-700 dark:text-slate-300 leading-relaxed mb-3">
      <strong>Oops!</strong> The page you're looking for seems to have wandered off into the digital unknown.
    </p>
    <p className="text-slate-600 dark:text-slate-400 text-sm">
      Don't worry though! You can explore our <strong>25,000+ AI tools directory</strong>, read our 
      <strong> AI blog</strong>, or return to the homepage.
    </p>
  </div>
);

// Extracted QuickLinksSection component
const QuickLinksSection: React.FC = () => (
  <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-slate-200 dark:border-slate-700 mb-8">
    <h3 className="font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center justify-center gap-2">
      <Compass className="w-5 h-5 text-purple-500" />
      Explore TooliQ.ai
    </h3>
    <div className="grid sm:grid-cols-3 gap-3">
      {QUICK_LINKS.map((link, index) => (
        <QuickLink key={index} link={link} />
      ))}
    </div>
  </div>
);

// Extracted QuickLink component
interface QuickLinkProps {
  link: typeof QUICK_LINKS[number];
}

const QuickLink: React.FC<QuickLinkProps> = ({ link }) => (
  <Link
    to={link.path}
    className="group flex items-center justify-center gap-2 p-3 bg-slate-50 dark:bg-slate-700/50 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200"
    aria-label={`Navigate to ${link.label}`}
  >
    <span className="text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
      {link.icon}
    </span>
    <span className="text-sm font-medium text-slate-700 dark:text-slate-300 group-hover:text-purple-600 dark:group-hover:text-purple-400">
      {link.label}
    </span>
  </Link>
);

// Extracted PrimaryActions component
interface PrimaryActionsProps {
  onGoBack: () => void;
}

const PrimaryActions: React.FC<PrimaryActionsProps> = ({ onGoBack }) => (
  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
    <Link
      to="/"
      className="group inline-flex items-center justify-center gap-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto"
      aria-label="Return to TooliQ.ai homepage"
    >
      <Home className="w-5 h-5" />
      Back to Homepage
      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
    </Link>
    
    <button
      onClick={onGoBack}
      className="inline-flex items-center justify-center gap-2 bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-700 text-slate-700 dark:text-slate-300 font-medium py-4 px-6 rounded-2xl border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 shadow-lg hover:shadow-xl transition-all duration-200 backdrop-blur-sm w-full sm:w-auto"
      aria-label="Go back to previous page"
    >
      ← Go Back
    </button>
  </div>
);

// Extracted AIToolsCTA component
const AIToolsCTA: React.FC = () => (
  <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl border border-green-200 dark:border-green-800">
    <div className="flex items-center justify-center gap-3 mb-3">
      <Sparkles className="w-5 h-5 text-green-600 dark:text-green-400" />
      <h4 className="font-semibold text-green-800 dark:text-green-300">
        Discover AI Tools Instead
      </h4>
    </div>
    <p className="text-sm text-green-700 dark:text-green-400 mb-3">
      While you're here, why not explore our comprehensive AI tools directory?
    </p>
    <Link
      to="/tools"
      className="inline-flex items-center gap-2 text-green-600 dark:text-green-400 font-medium hover:text-green-700 dark:hover:text-green-300 transition-colors text-sm"
      aria-label="Search through AI tools directory"
    >
      <Search className="w-4 h-4" />
      Browse 25,000+ AI Tools →
    </Link>
  </div>
);

// Extracted HelpSection component
const HelpSection: React.FC = () => (
  <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-2xl border border-blue-200 dark:border-blue-800">
    <p className="text-sm text-blue-800 dark:text-blue-300">
      Need help?{' '}
      <Link 
        to="/contact" 
        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        aria-label="Contact TooliQ support team"
      >
        Contact our support team
      </Link>
      {' '}or{' '}
      <Link 
        to="/faq" 
        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
        aria-label="View frequently asked questions"
      >
        check our FAQ
      </Link>
    </p>
  </div>
);

// Extracted StatsSection component
const StatsSection: React.FC = () => (
  <div className="mt-8 grid grid-cols-2 gap-4">
    {STATS.map((stat, index) => (
      <StatCard key={index} stat={stat} />
    ))}
  </div>
);

// Extracted StatCard component
interface StatCardProps {
  stat: typeof STATS[number];
}

const StatCard: React.FC<StatCardProps> = ({ stat }) => {
  const colorClasses = {
    purple: 'text-purple-600 dark:text-purple-400',
    blue: 'text-blue-600 dark:text-blue-400',
    green: 'text-green-600 dark:text-green-400'
  };

  return (
    <div className="text-center p-4 bg-white/50 dark:bg-gray-800/50 rounded-xl border border-gray-200/50 dark:border-gray-700/50">
      <div className={`text-xl font-bold ${colorClasses[stat.color] || colorClasses.purple}`}>
        {stat.value}
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-400">
        {stat.label}
      </div>
    </div>
  );
};

// Extracted DecorativeSection component
const DecorativeSection: React.FC = () => (
  <div className="mt-8 text-center">
    <p className="text-sm text-slate-500 dark:text-slate-500">
      Still lost? Try using our{' '}
      <Link 
        to="/search" 
        className="text-purple-600 dark:text-purple-400 hover:underline font-medium"
        aria-label="Use search functionality"
      >
        search feature
      </Link>
    </p>
  </div>
);

// Extracted BackgroundAnimation component
const BackgroundAnimation: React.FC = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none">
    <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-r from-purple-200/20 to-blue-200/20 dark:from-purple-600/10 dark:to-blue-600/10 rounded-full blur-3xl animate-pulse"></div>
    <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-r from-orange-200/20 to-red-200/20 dark:from-orange-600/10 dark:to-red-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
  </div>
);

export default React.memo(NotFound);